import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { LayoutGrid } from "lucide-react";
import { api, errMsg } from "../api";
import { FormField, PrimaryButton } from "./FormField";
import { useRequireAuth } from "../hooks/useRequireAuth";

const ZoneForm = ({ values, set, zones, busy, onSubmit }) => (
  <div className="flex flex-col gap-4">
    <FormField
      label="Zone Name"
      data-testid="zone-name-input"
      value={values.name}
      onChange={(e) => set("name", e.target.value)}
      placeholder="Level 1 — North"
    />
    <div className="grid grid-cols-2 gap-4">
      <FormField
        label="From Space"
        type="number"
        data-testid="zone-from-input"
        value={values.from}
        onChange={(e) => set("from", e.target.value)}
      />
      <FormField
        label="To Space"
        type="number"
        data-testid="zone-to-input"
        value={values.to}
        onChange={(e) => set("to", e.target.value)}
      />
    </div>
    <p className="text-sm text-[#A3A3A3]">
      Leave the name empty to remove the label from that range.
      {zones.length > 0 && ` Existing zones: ${zones.join(", ")}.`}
    </p>
    <PrimaryButton data-testid="zone-save-btn" disabled={busy || !values.from || !values.to} onClick={onSubmit}>
      Save Zone
    </PrimaryButton>
  </div>
);

export const ZoneDialog = ({ spaces, refresh }) => {
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState({ name: "", from: "1", to: "10" });
  const [busy, setBusy] = useState(false);
  const set = (key, value) => setValues((v) => ({ ...v, [key]: value }));
  const zones = [...new Set(spaces.map((s) => s.zone).filter(Boolean))];
  const requireAuth = useRequireAuth();

  const submit = async () => {
    setBusy(true);
    try {
      const res = await api.setZone({
        name: values.name,
        from_number: Number(values.from),
        to_number: Number(values.to),
      });
      toast.success(
        res.zone
          ? `Spaces ${res.from_number}-${res.to_number} labelled "${res.zone}"`
          : `Zone label cleared on spaces ${res.from_number}-${res.to_number}`
      );
      setOpen(false);
      await refresh();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => setOpen(o && requireAuth())}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          data-testid="zone-settings-btn"
          className="h-9 rounded-sm border-[#262626] bg-transparent hover:bg-[#1F1F1F] uppercase tracking-widest text-xs gap-2"
        >
          <LayoutGrid className="w-4 h-4" />
          Zones
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#141414] border-[#262626] rounded-sm" data-testid="zone-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">Zone Labels</DialogTitle>
          <DialogDescription className="text-sm text-[#A3A3A3]">
            Name a range of spaces so the grid matches the real lot.
          </DialogDescription>
        </DialogHeader>
        <ZoneForm values={values} set={set} zones={zones} busy={busy} onSubmit={submit} />
      </DialogContent>
    </Dialog>
  );
};
